/**
 * Sprint 0 acceptance: the frontend can reach the Worker's health endpoint and
 * the D1 / R2 bindings report back.
 *
 * This is a development aid, not a product screen. It is removed once real
 * screens exist.
 */

import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';

interface HealthResponse {
  status: 'ok' | 'degraded';
  environment: string;
  checks: {
    database: { ok: boolean; error?: string };
    storage: { ok: boolean; error?: string };
  };
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; data: HealthResponse }
  | { kind: 'failed'; message: string };

export function HealthCheck() {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();

    api
      .get<HealthResponse>('/api/health', controller.signal)
      .then((data) => setState({ kind: 'ready', data }))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const message =
          err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR');
        setState({ kind: 'failed', message });
      });

    return () => controller.abort();
  }, []);

  return (
    <section>
      <h1>Sistem Durumu</h1>

      {state.kind === 'loading' && (
        <p style={{ color: 'var(--pp-ink-muted)' }}>Kontrol ediliyor…</p>
      )}

      {state.kind === 'failed' && <p role="alert">{state.message}</p>}

      {state.kind === 'ready' && (
        <dl style={{ marginTop: 'var(--pp-space-4)', lineHeight: 2 }}>
          <Row label="Durum" value={state.data.status} />
          <Row label="Ortam" value={state.data.environment} />
          <Row
            label="Veritabanı (D1)"
            value={state.data.checks.database.ok ? 'bağlı' : 'bağlı değil'}
          />
          <Row
            label="Depolama (R2)"
            value={state.data.checks.storage.ok ? 'bağlı' : 'bağlı değil'}
          />
        </dl>
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--pp-space-3)' }}>
      <dt style={{ minWidth: 160, color: 'var(--pp-ink-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: 500 }}>{value}</dd>
    </div>
  );
}
